// External Library Imports
import { zodResolver } from "@hookform/resolvers/zod";
import { useRouter } from "@tanstack/react-router";
import { ArrowLeft, Loader2, Save, X } from "lucide-react";
import { useForm } from "react-hook-form";
import { toast } from "sonner";
//

// UI Component Imports
import { Button } from "@/components/ui/button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { DatePicker } from "@/components/shared/date-picker";

// Type and Schema Imports
import { PriorityEnum, TodoStatusEnum, UpdateTodo } from "@/types/todos.types";
import { TodoFormValues, todoSchema } from "../schemas/todo.schema";

// Query/Mutation Imports
import { useCreateTodo } from "../queries/todos.create.queries";
import { useUpdateTodo } from "../queries/todos.update.queries";

// Types and Interfaces
interface TodoFormProps {
  edit?: boolean;
  todo?: { id: string } & UpdateTodo;
}

/**
 * TodoForm Component
 * Handles both creation and editing of todo items
 * @param {TodoFormProps} props - Component props
 */
export const TodoForm = ({ edit, todo }: TodoFormProps) => {
  const router = useRouter();

  // Mutations
  const { mutateAsync: createTodo, isPending: isCreating } = useCreateTodo();
  const { mutateAsync: updateTodo, isPending: isUpdating } = useUpdateTodo(
    todo?.id ?? "",
  );

  // Form initialization
  const form = useForm<TodoFormValues>({
    resolver: zodResolver(todoSchema),
    defaultValues: {
      title: todo?.title ?? "",
      description: todo?.description ?? "",
      status: todo?.status ?? TodoStatusEnum.enum.todo,
      priority: todo?.priority ?? PriorityEnum.enum.low,
      dueDate: (typeof todo?.dueDate === "string" ? todo?.dueDate : undefined) ?? undefined,
    },
  });

  /**
   * Handles form submission and navigation
   * @param {TodoFormValues} data - Form data
   */
  const onSubmit = async (data: TodoFormValues): Promise<void> => {
    handleSaveData(data);
    router.navigate({ to: "/dashboard/todos" });
  };

  /**
   * Processes form data and makes API calls
   * @param {TodoFormValues} data - Form data to be saved
   */
  const handleSaveData = async (data: TodoFormValues): Promise<void> => {
    try {
      const formattedData = {
        ...data,
        dueDate: data.dueDate ?? undefined,
      };

      if (edit && todo) {
        await updateTodo({ ...formattedData });
        toast.success("待办更新成功");
      } else {
        await createTodo({ ...formattedData });
        toast.success("待办创建成功");
      }
    } catch (error) {
      toast.error(
        error instanceof Error
          ? error.message
          : "保存待办时发生错误",
      );
    }
  };

  /**
   * Renders a form field (text input or textarea)
   */
  const renderTextField = (
    name: keyof TodoFormValues,
    label: string,
    placeholder: string,
    isTextarea = false,
  ) => (
    <FormField
      control={form.control}
      name={name}
      render={({ field }) => (
        <FormItem>
          <FormLabel>{label}</FormLabel>
          <FormControl>
            {isTextarea ? (
              <Textarea
                placeholder={placeholder}
                className="min-h-[100px]"
                {...field}
              />
            ) : (
              <Input placeholder={placeholder} {...field} />
            )}
          </FormControl>
          <FormMessage />
        </FormItem>
      )}
    />
  );

  /**
   * Renders a select field
   */
  const renderSelectField = (
    name: "status" | "priority",
    label: string,
    options: string[],
    formatOption?: (option: string) => string,
  ) => (
    <FormField
      control={form.control}
      name={name}
      render={({ field }) => (
        <FormItem>
          <FormLabel>{label}</FormLabel>
          <Select onValueChange={field.onChange} defaultValue={field.value}>
            <FormControl>
              <SelectTrigger>
                <SelectValue placeholder={`选择${label}`} />
              </SelectTrigger>
            </FormControl>
            <SelectContent>
              {options.map((option) => (
                <SelectItem key={option} value={option}>
                  {formatOption ? formatOption(option) : option}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <FormMessage />
        </FormItem>
      )}
    />
  );

  return (
    <Form {...form}>
      <form
        onSubmit={form.handleSubmit(onSubmit)}
        className="space-y-8"
        aria-label={`${edit ? "编辑" : "创建"} 待办表单`}
      >
        {renderTextField("title", "标题", "请输入待办标题")}
        {renderTextField(
          "description",
          "描述",
          "请输入待办描述",
          true,
        )}

        <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
          {renderSelectField(
            "status",
            "状态",
            TodoStatusEnum.options,
            (status) =>
              ({
                todo: "待办",
                in_progress: "进行中",
                completed: "已完成",
                archived: "已归档",
              } as Record<string, string>)[status] ?? status,
          )}
          {renderSelectField(
            "priority",
            "优先级",
            PriorityEnum.options,
            (priority) => ({ high: "高", medium: "中", low: "低" } as Record<string, string>)[priority] ?? priority,
          )}
        </div>

        <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
          <FormField
            control={form.control}
            name="dueDate"
            render={({ field }) => (
              <FormItem>
                <FormLabel>截止日期</FormLabel>
                <FormControl>
                  <DatePicker
                    date={field.value ? new Date(field.value) : undefined}
                    setDate={(date) => {
                      if (date) {
                        field.onChange(date.toISOString());
                      }
                    }}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        <div className="flex items-center justify-between gap-4 pt-4">
          <Button
            type="button"
            variant="outline"
            onClick={() => router.navigate({ to: "/dashboard/todos" })}
            aria-label="Back to todo list"
          >
            <ArrowLeft className="h-4 w-4" />
            返回列表
          </Button>

          <div className="flex items-center gap-2">
            {form.formState.isDirty && (
              <Button
                type="button"
                variant="outline"
                onClick={() => form.reset()}
                aria-label="Reset form"
              >
                <X className="h-4 w-4" />
                重置
              </Button>
            )}
            <Button
              type="submit"
              disabled={isCreating || isUpdating}
              className="bg-neutral-950 text-white hover:bg-neutral-800"
              aria-label={edit ? "Update todo" : "Create todo"}
            >
              {(isCreating || isUpdating) && (
                <Loader2 className="h-4 w-4 animate-spin" />
              )}
              <Save className="h-4 w-4" />
              {edit ? "更新待办" : "新增待办"}
            </Button>
          </div>
        </div>
      </form>
    </Form>
  );
};
